import React, { useEffect, useState, useRef, useMemo } from "react";
import {
    Form, Input, Select, Radio, InputNumber,
    Button, Card, Space, message, Divider, Tabs, Spin,
} from "antd";
import { useNavigate, useParams } from "react-router-dom";
import { useAppSelector } from "../../../store";
import emailCampaignApi from "../../../api/emailCampaignApi";
// react-quill-new = Quill 2.0.3. KHÔNG dùng react-quill@2 (Quill 1.3.7): bug
// lệch index ở emoji (cặp surrogate) làm trôi biên in đậm/link + chèn dấu cách
// giữa từ — email marketing toàn emoji nên dính rất nặng.
import ReactQuill from "react-quill-new";
import "react-quill-new/dist/quill.snow.css";
import EmailImageInsertModal from "./email-image-insert-modal";
import { normalizeEmailHtml } from "./email-html-normalize";

const { TextArea } = Input;

// Quill toolbar config — image handler opens custom modal (URL or file upload)
const buildQuillModules = (openImageModal) => ({
    toolbar: {
        container: [
            [{ header: [1, 2, 3, false] }],
            ["bold", "italic", "underline", "strike"],
            [{ color: [] }, { background: [] }],
            [{ align: [] }],
            ["link", "image"],
            [{ list: "ordered" }, { list: "bullet" }],
            ["blockquote"],
            ["clean"],
        ],
        handlers: {
            image: () => openImageModal(),
        },
    },
});

function buildAudience(audienceMode, filters, rawIds) {
    if (audienceMode === "all") return { mode: "all" };
    if (audienceMode === "ids") {
        const userIds = rawIds
            .split(/[\s,\n]+/)
            .map((s) => parseInt(s.trim(), 10))
            .filter((n) => n > 0);
        return { mode: "ids", userIds };
    }
    const f = {};
    if (filters.verified !== undefined) f.verified = filters.verified;
    if (filters.active !== undefined) f.active = filters.active;
    if (filters.role) f.role = filters.role;
    if (filters.isLiveUser !== undefined) f.isLiveUser = filters.isLiveUser;
    if (filters.isBuffUser !== undefined) f.isBuffUser = filters.isBuffUser;
    if (filters.minCash != null) f.minCash = filters.minCash;
    if (filters.maxCash != null) f.maxCash = filters.maxCash;
    if (filters.minRank != null) f.minRank = filters.minRank;
    if (filters.maxRank != null) f.maxRank = filters.maxRank;
    return { mode: "filtered", filters: f };
}

function EmailCampaignForm() {
    const navigate = useNavigate();
    const { id } = useParams(); // defined → edit mode
    const isEdit = Boolean(id);
    const auth = useAppSelector((state) => state.auth);
    const [form] = Form.useForm();

    const quillRef = useRef(null);
    const [imageModalOpen, setImageModalOpen] = useState(false);
    // Memoize modules so Quill doesn't remount toolbar on every render
    // eslint-disable-next-line react-hooks/exhaustive-deps
    const quillModules = useMemo(() => buildQuillModules(() => setImageModalOpen(true)), []);
    const [audienceMode, setAudienceMode] = useState("all");
    const [previewCount, setPreviewCount] = useState(null);
    const [previewLoading, setPreviewLoading] = useState(false);
    const [submitLoading, setSubmitLoading] = useState(false);
    const [testEmail, setTestEmail] = useState("");
    const [testLoading, setTestLoading] = useState(false);
    const [filters, setFilters] = useState({});
    const [rawIds, setRawIds] = useState("");
    const [htmlBody, setHtmlBody] = useState("");
    const [editorTab, setEditorTab] = useState("wysiwyg");

    // Chuẩn hoá ở lúc gửi/lưu (KHÔNG ở onChange) để tránh bug nhảy con trỏ
    const normalizeHtml = normalizeEmailHtml;
    const [pageLoading, setPageLoading] = useState(isEdit);

    // Load campaign data in edit mode
    useEffect(() => {
        if (!isEdit) return;
        (async () => {
            try {
                const { data } = await emailCampaignApi.getCampaign(id);
                const c = data.data.campaign;
                form.setFieldsValue({ name: c.name, subject: c.subject });
                setHtmlBody(c.htmlBody || "");
                // audiencePayload có thể là string (nếu MySQL trả về dạng raw text)
                const raw = c.audiencePayload;
                const ap = (typeof raw === "string" ? JSON.parse(raw) : raw) || {};
                setAudienceMode(ap.mode || "all");
                if (ap.mode === "filtered") setFilters(ap.filters || {});
                if (ap.mode === "ids") setRawIds((ap.userIds || []).join(", "));
            } catch {
                message.error("Không thể tải dữ liệu chiến dịch");
            } finally {
                setPageLoading(false);
            }
        })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [id]);

    const handleTestSend = async () => {
        const values = form.getFieldsValue();
        if (!values.subject || !htmlBody) {
            message.warning("Vui lòng nhập tiêu đề và nội dung trước");
            return;
        }
        if (!testEmail) { message.warning("Nhập email nhận thử"); return; }
        try {
            setTestLoading(true);
            await emailCampaignApi.testSend({
                toEmail: testEmail,
                subject: values.subject,
                htmlBody: normalizeHtml(htmlBody),
            });
            message.success(`Đã gửi thử tới ${testEmail}`);
        } catch (err) {
            message.error(err?.response?.data?.message || "Gửi thử thất bại");
        } finally {
            setTestLoading(false);
        }
    };

    const handlePreview = async () => {
        try {
            setPreviewLoading(true);
            const audience = buildAudience(audienceMode, filters, rawIds);
            const { data } = await emailCampaignApi.preview(audience);
            setPreviewCount(data.data.targetUserCount);
        } catch {
            message.error("Không thể lấy số lượng user");
        } finally {
            setPreviewLoading(false);
        }
    };

    const handleSubmit = async (values) => {
        if (!htmlBody || !htmlBody.trim()) {
            message.warning("Vui lòng nhập nội dung email");
            return;
        }
        try {
            setSubmitLoading(true);
            const audience = buildAudience(audienceMode, filters, rawIds);
            const normalizedBody = normalizeHtml(htmlBody);
            if (isEdit) {
                await emailCampaignApi.updateCampaign(id, {
                    name: values.name,
                    subject: values.subject,
                    htmlBody: normalizedBody,
                    audience,
                });
                message.success("Đã cập nhật chiến dịch");
                navigate(`/email/${id}`);
            } else {
                await emailCampaignApi.createCampaign({
                    name: values.name,
                    subject: values.subject,
                    htmlBody: normalizedBody,
                    audience,
                    createdBy: auth.userInfo?.email || auth.userInfo?.name || "admin",
                });
                message.success("Tạo chiến dịch thành công");
                navigate("/email");
            }
        } catch (err) {
            message.error(err?.response?.data?.message || (isEdit ? "Cập nhật thất bại" : "Tạo thất bại"));
        } finally {
            setSubmitLoading(false);
        }
    };

    if (pageLoading) {
        return (
            <div className="flex-1 flex items-center justify-center">
                <Spin size="large" />
            </div>
        );
    }

    return (
        <div className="flex-1 p-6 overflow-auto max-w-4xl">
            <div className="flex items-center gap-3 mb-6">
                <Button onClick={() => navigate(isEdit ? `/email/${id}` : "/email")}>← Quay lại</Button>
                <h1 className="text-xl font-semibold m-0">
                    {isEdit ? "Sửa chiến dịch email" : "Tạo chiến dịch email"}
                </h1>
            </div>

            <Form form={form} layout="vertical" onFinish={handleSubmit}>
                <Card title="Thông tin chiến dịch" className="mb-4">
                    <Form.Item name="name" label="Tên chiến dịch" rules={[{ required: true }]}>
                        <Input placeholder="Ví dụ: Sale 11/11 2026" />
                    </Form.Item>
                    <Form.Item name="subject" label="Tiêu đề email (subject)" rules={[{ required: true }]}>
                        <Input placeholder="Ví dụ: 🔥 SALE 11/11 - HOÀN TIỀN TỚI 50%" />
                    </Form.Item>

                    {/* Email body editor */}
                    <Form.Item label="Nội dung email" required>
                        <Tabs
                            activeKey={editorTab}
                            onChange={setEditorTab}
                            size="small"
                            items={[
                                {
                                    key: "wysiwyg",
                                    label: "Soạn thảo",
                                    children: (
                                        <div style={{ border: "1px solid #d9d9d9", borderRadius: 6, overflow: "hidden" }}>
                                            <ReactQuill
                                                ref={quillRef}
                                                theme="snow"
                                                value={htmlBody}
                                                onChange={setHtmlBody}
                                                modules={quillModules}
                                                style={{ minHeight: 280 }}
                                            />
                                        </div>
                                    ),
                                },
                                {
                                    key: "html",
                                    label: "HTML",
                                    children: (
                                        <TextArea
                                            rows={12}
                                            value={htmlBody}
                                            onChange={(e) => setHtmlBody(e.target.value)}
                                            placeholder="<html>...</html>"
                                            style={{ fontFamily: "monospace", fontSize: 12 }}
                                        />
                                    ),
                                },
                                {
                                    key: "preview",
                                    label: "Preview",
                                    children: (
                                        <div
                                            className="border rounded p-3 bg-surface overflow-auto"
                                            style={{ minHeight: 200, maxHeight: 400 }}
                                            dangerouslySetInnerHTML={{ __html: htmlBody }}
                                        />
                                    ),
                                },
                            ]}
                        />
                    </Form.Item>

                    <Form.Item label="Gửi thử">
                        <Space.Compact style={{ width: "100%" }}>
                            <Input
                                placeholder="email@example.com"
                                value={testEmail}
                                onChange={(e) => setTestEmail(e.target.value)}
                                style={{ maxWidth: 280 }}
                            />
                            <Button onClick={handleTestSend} loading={testLoading}>
                                Gửi thử
                            </Button>
                        </Space.Compact>
                    </Form.Item>
                </Card>

                <Card title="Đối tượng nhận email" className="mb-4">
                    <Form.Item label="Chọn đối tượng">
                        <Radio.Group value={audienceMode} onChange={(e) => setAudienceMode(e.target.value)}>
                            <Radio value="all">Tất cả user</Radio>
                            <Radio value="filtered">Bộ lọc</Radio>
                            <Radio value="ids">Danh sách ID</Radio>
                        </Radio.Group>
                    </Form.Item>

                    {audienceMode === "filtered" && (
                        <>
                            <Divider orientation="left" plain>Bộ lọc</Divider>
                            <div className="grid grid-cols-2 gap-x-6">
                                <Form.Item label="Đã xác thực email">
                                    <Select
                                        allowClear
                                        placeholder="Bất kỳ"
                                        value={filters.verified}
                                        onChange={(v) => setFilters((f) => ({ ...f, verified: v }))}
                                        options={[{ value: true, label: "Có" }, { value: false, label: "Chưa" }]}
                                    />
                                </Form.Item>
                                <Form.Item label="Đang hoạt động">
                                    <Select
                                        allowClear
                                        placeholder="Bất kỳ"
                                        value={filters.active}
                                        onChange={(v) => setFilters((f) => ({ ...f, active: v }))}
                                        options={[{ value: true, label: "Có" }, { value: false, label: "Bị khoá" }]}
                                    />
                                </Form.Item>
                                <Form.Item label="Vai trò">
                                    <Select
                                        allowClear
                                        placeholder="Bất kỳ"
                                        value={filters.role}
                                        onChange={(v) => setFilters((f) => ({ ...f, role: v }))}
                                        options={[
                                            { value: "user", label: "User" },
                                            { value: "admin", label: "Admin" },
                                            { value: "mod", label: "Mod" },
                                        ]}
                                    />
                                </Form.Item>
                                <Form.Item label="Live User">
                                    <Select
                                        allowClear
                                        placeholder="Bất kỳ"
                                        value={filters.isLiveUser}
                                        onChange={(v) => setFilters((f) => ({ ...f, isLiveUser: v }))}
                                        options={[{ value: true, label: "Có" }, { value: false, label: "Không" }]}
                                    />
                                </Form.Item>
                                <Form.Item label="Cash tối thiểu (VNĐ)">
                                    <InputNumber
                                        style={{ width: "100%" }}
                                        min={0}
                                        value={filters.minCash}
                                        placeholder="0"
                                        onChange={(v) => setFilters((f) => ({ ...f, minCash: v }))}
                                    />
                                </Form.Item>
                                <Form.Item label="Cash tối đa (VNĐ)">
                                    <InputNumber
                                        style={{ width: "100%" }}
                                        min={0}
                                        value={filters.maxCash}
                                        placeholder="Không giới hạn"
                                        onChange={(v) => setFilters((f) => ({ ...f, maxCash: v }))}
                                    />
                                </Form.Item>
                                <Form.Item label="Rank tối thiểu">
                                    <InputNumber
                                        style={{ width: "100%" }}
                                        min={0}
                                        value={filters.minRank}
                                        onChange={(v) => setFilters((f) => ({ ...f, minRank: v }))}
                                    />
                                </Form.Item>
                                <Form.Item label="Rank tối đa">
                                    <InputNumber
                                        style={{ width: "100%" }}
                                        min={0}
                                        value={filters.maxRank}
                                        onChange={(v) => setFilters((f) => ({ ...f, maxRank: v }))}
                                    />
                                </Form.Item>
                            </div>
                        </>
                    )}

                    {audienceMode === "ids" && (
                        <Form.Item label="Danh sách User ID (phân cách bởi dấu phẩy hoặc xuống dòng)">
                            <TextArea
                                rows={5}
                                placeholder="1234, 5678&#10;9012"
                                value={rawIds}
                                onChange={(e) => setRawIds(e.target.value)}
                            />
                        </Form.Item>
                    )}

                    <div className="flex items-center gap-3 mt-2">
                        <Button onClick={handlePreview} loading={previewLoading}>
                            Xem số user sẽ nhận
                        </Button>
                        {previewCount !== null && (
                            <span className="text-blue-600 font-medium">
                                Sẽ gửi đến <strong>{previewCount.toLocaleString()}</strong> users
                            </span>
                        )}
                    </div>
                </Card>

                <Space>
                    <Button onClick={() => navigate(isEdit ? `/email/${id}` : "/email")}>Hủy</Button>
                    <Button type="primary" htmlType="submit" loading={submitLoading}>
                        {isEdit ? "Lưu thay đổi" : "Tạo chiến dịch (nháp)"}
                    </Button>
                </Space>
            </Form>

            {/* Modal chèn ảnh — URL hoặc tải lên */}
            <EmailImageInsertModal
                open={imageModalOpen}
                onClose={() => setImageModalOpen(false)}
                onInsert={(url) => {
                    const editor = quillRef.current?.getEditor();
                    if (!editor) return;
                    const range = editor.getSelection(true);
                    editor.insertEmbed(range.index, "image", url);
                    editor.setSelection(range.index + 1);
                }}
            />
        </div>
    );
}

export default EmailCampaignForm;
